import { InfoIcon } from '@phosphor-icons/react';
import { Fragment } from 'react';

import { Tooltip } from '@/components/common/tooltip';
import { OverlayPattern } from '@/components/feedback/overlay';

import { formatApplicabilitySummary, formatDiscountAmount } from '../../format';
import type { Applicability, DiscountFormat, QuantityRuleFormValue } from '../../types';

import styles from './discount-overview.module.scss';

const NOUN = { device: 'Devices', bundle: 'Bundles', customer: 'Customers' } as const;

const BEHAVIOUR_LABEL = {
  limit_to_original: 'Limit to original discount',
  allow_higher: 'Allow higher discounts',
} as const;

// Behaviour-dependent tooltip on the Amount/Percentage row (Figma 8328-1021599; no frame for the copy).
const QTY_TOOLTIP = {
  limit_to_original: 'Quantity discounts value are capped at the original discount value.',
  allow_higher: 'Quantity discounts value can exceed the original discount value.',
} as const;

export type DiscountOverviewProps = {
  name: string;
  discountFormat?: DiscountFormat;
  value: string;
  applicability: Applicability | undefined;
  trigger?: { label: string; window: string | null };
  quantityRule?: QuantityRuleFormValue;
};

/** Live "Discount Overview" panel for the Create wizard: cream patterned header band over a
 *  label/value summary. A Quantity-Based rule renders a mini tier table (green range chips +
 *  per-unit values, an "Above …" divider for allow-higher) mirroring Figma; Fixed is a plain row. */
export function DiscountOverview({
  name,
  discountFormat,
  value,
  applicability,
  trigger,
  quantityRule,
}: DiscountOverviewProps) {
  const amountLabel = discountFormat === 'percentage' ? 'Percentage' : 'Amount';
  const amountDisplay = formatDiscountAmount(discountFormat, value);
  const qtyBased = quantityRule?.type === 'quantity_based' ? quantityRule : null;
  const noun = applicability ? NOUN[applicability.type] : 'Devices';
  const sortedTiers = qtyBased
    ? [...qtyBased.tiers].sort((a, b) => Number(a.minQty) - Number(b.minQty))
    : [];
  // allow-higher: the divider is a strict partition — every tier at or under the Step-1 value,
  // then every tier over it — not just a marker dropped at the first tier that happened to cross
  // it in minQty order (117113). Values aren't monotonic under this behaviour (tier order: 3%,
  // 78%, 2% is valid), so a later non-exceeding tier must render back in the capped group rather
  // than trailing the divider with nothing to pull it back out.
  const isAllowHigher = qtyBased?.tierBehavior === 'allow_higher';
  const exceedsStepValue = (t: (typeof sortedTiers)[number]) => Number(t.value) > Number(value);
  const cappedTiers = isAllowHigher ? sortedTiers.filter((t) => !exceedsStepValue(t)) : sortedTiers;
  const exceedingTiers = isAllowHigher ? sortedTiers.filter(exceedsStepValue) : [];
  const tiers = [...cappedTiers, ...exceedingTiers];

  return (
    <aside aria-label="Discount Overview" className={styles.card}>
      <div className={styles.header}>
        <OverlayPattern className={styles.pattern} />
        <h2 className={styles.heading}>Discount Overview</h2>
      </div>
      <dl className={styles.body}>
        <div className={styles.row}>
          <dt className={styles.label}>Discount Name</dt>
          <dd className={styles.value}>{name.trim() || '-'}</dd>
        </div>
        <div className={styles.row}>
          <dt className={styles.label}>{amountLabel}</dt>
          <dd className={styles.value}>
            {amountDisplay}
            {qtyBased ? (
              <Tooltip
                appearance="dark"
                placement="bottom"
                title={QTY_TOOLTIP[qtyBased.tierBehavior]}
              >
                <InfoIcon className={styles.infoIcon} size={16} />
              </Tooltip>
            ) : null}
          </dd>
        </div>
        <div className={styles.row}>
          <dt className={styles.label}>Applicable To</dt>
          <dd className={styles.value}>{formatApplicabilitySummary(applicability)}</dd>
        </div>
        <div className={styles.row}>
          <dt className={styles.label}>Trigger</dt>
          <dd className={styles.value}>
            {trigger ? (
              <>
                {trigger.label}
                {trigger.window ? <span className={styles.subValue}>{trigger.window}</span> : null}
              </>
            ) : (
              '-'
            )}
          </dd>
        </div>

        {qtyBased ? (
          <div className={styles.qtySection}>
            <dt className={styles.qtyHeading}>Quantity Rule</dt>
            <dd className={styles.qtyBehaviour}>{BEHAVIOUR_LABEL[qtyBased.tierBehavior]}</dd>
            <div className={styles.qtyTable}>
              <div className={styles.qtyTableHead}>
                <span>{noun}</span>
                <span>Discount</span>
              </div>
              {tiers.map((t, i) => (
                <Fragment key={`${t.minQty}-${t.maxQty}-${i}`}>
                  {i === cappedTiers.length && exceedingTiers.length > 0 ? (
                    <div className={styles.qtyDivider}>
                      <span>Above {amountDisplay}</span>
                    </div>
                  ) : null}
                  <div className={styles.qtyRow}>
                    <span className={styles.qtyChip}>
                      {t.minQty} - {t.maxQty} <span className={styles.qtyUnits}>units</span>
                    </span>
                    <span className={styles.qtyUnitValue}>
                      {formatDiscountAmount(discountFormat, t.value)}{' '}
                      <span className={styles.qtyPerUnit}>/unit</span>
                    </span>
                  </div>
                </Fragment>
              ))}
            </div>
          </div>
        ) : (
          <div className={styles.row}>
            <dt className={styles.label}>Quantity Rule</dt>
            <dd className={styles.value}>
              {quantityRule?.type === 'fixed' ? 'Fixed Discount' : '-'}
            </dd>
          </div>
        )}
      </dl>
    </aside>
  );
}
